import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { DesktopConfig, InvoiceConfig } from "../../scenario/types";
import { Desktop } from "../Desktop";
import { WindowTitlebar } from "../WindowTitlebar";
import { audio } from "../../lib/audio";
import { activeTtsLang } from "../../lib/locale";
import { useSim } from "../../state/store";
import type { MfitDone, MfitGuideFn } from "../mfit/shared";
import "./invoice.css";

/**
 * "The Invoice That Changed" — supplier bank-change / invoice fraud (BEC) at the
 * Bellmont Museum. One course-local device with modes. The whole course now runs
 * on the south-wing desk PC inside the reused Windows Desktop (briefing, film,
 * finance + guided inspection, payment, incident); the wall TV is used only for
 * the final summary; the attacker intro is a narrated card. Nothing is really
 * sent, called or paid. Decisions never hint the answer (neutral cards, sweep).
 */

const DOT = "\u00b7";
const DESK_APPS = [
  { id: "outlook", label: "Mail", icon: "outlook", opens: "outlook" },
  { id: "pdf", label: "Invoices", icon: "pdf", opens: "pdf" },
  { id: "files", label: "Procurement", icon: "files", opens: "files" },
  { id: "phone", label: "Phone", icon: "phone", opens: "phone" },
];

function recorded(): Set<string> {
  return new Set(useSim.getState().outcomes.map((o) => o.eventId).filter(Boolean) as string[]);
}

/** Animated "someone is speaking" bars (matches the guide card's voice strip). */
function VoiceBars() {
  return (
    <span className="inv-voicebars" aria-hidden="true"><i /><i /><i /><i /></span>
  );
}

/** One next-step chip, placed OUTSIDE the target to its right, arrow pointing
 * back at it ("<- Click here"). Clicking the chip runs the same action as the
 * target. Only ever render ONE at a time (one target per step). */
function Chip({ targetRef, onClick, label = "Click here" }: { targetRef: React.RefObject<HTMLElement | null>; onClick?: () => void; label?: string }) {
  const [pos, setPos] = useState<{ left: number; top: number; side: "right" | "left" } | null>(null);
  useEffect(() => {
    let raf = 0;
    const place = () => {
      const el = targetRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!r.width) return;
      const gap = 12, chipW = 160, margin = 8;
      let side: "right" | "left" = "right";
      let left = r.right + gap;
      if (left + chipW > window.innerWidth - margin) { side = "left"; left = r.left - gap; }
      setPos({ left, top: r.top + r.height / 2, side });
    };
    place();
    const onMove = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(place); };
    window.addEventListener("resize", onMove, true);
    window.addEventListener("scroll", onMove, true);
    const iv = window.setInterval(place, 300);
    return () => { window.removeEventListener("resize", onMove, true); window.removeEventListener("scroll", onMove, true); window.clearInterval(iv); cancelAnimationFrame(raf); };
  }, [targetRef]);
  if (!pos) return null;
  // position:fixed in a top-level portal -> can never change a container's layout
  // or add a scrollbar; flips to the left of the target when the right has no room.
  return createPortal(
    <button type="button" className={`inv-chip inv-chip--${pos.side}`} style={{ left: pos.left, top: pos.top }} onClick={onClick} aria-label={label} tabIndex={-1}>
      {pos.side === "right"
        ? (<><i className="inv-chip-arrow" aria-hidden="true">{"\u2190"}</i><span>{label}</span></>)
        : (<><span>{label}</span><i className="inv-chip-arrow" aria-hidden="true">{"\u2192"}</i></>)}
    </button>,
    document.body,
  );
}
/** A target button + its optional next-step chip (portaled next to it). */
function Next({ on, onClick, cls = "inv-btn inv-btn--primary", chip = "Click here", children }: { on: boolean; onClick: () => void; cls?: string; chip?: string; children: React.ReactNode }) {
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <span className="inv-next">
      <button ref={ref} type="button" className={`${cls}${on ? " cf-pulse-target" : ""}`} onClick={onClick}>{children}</button>
      {on && <Chip targetRef={ref} onClick={onClick} label={chip} />}
    </span>
  );
}

const ICON_PATHS: Record<string, string> = {
  phone: "M4 4h3l2 5-2 1a11 11 0 0 0 5 5l1-2 5 2v3a1 1 0 0 1-1 1A16 16 0 0 1 3 5a1 1 0 0 1 1-1z",
  mail: "M3 6h18v12H3z M3 6l9 7 9-7",
  flag: "M5 3v18 M5 4h12l-2.5 4 2.5 4H5",
  chat: "M4 5h16v10H9l-4 4v-4H4z",
  help: "M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18z M9.5 9a2.5 2.5 0 1 1 3.3 2.4c-.8.3-1.3 1-1.3 1.8v.4 M12 16.5h.01",
};
function Icon({ name }: { name: keyof typeof ICON_PATHS }) {
  return (
    <svg className="inv-ic" viewBox="0 0 24 24" aria-hidden="true">
      {ICON_PATHS[name].split(" M").map((d, i) => <path key={i} d={(i ? "M" : "") + d} />)}
    </svg>
  );
}

type InspectItem = NonNullable<NonNullable<InvoiceConfig["finance"]>["inspection"]>[number];

/** Per-item "what to look for" modal with an animated SVG (a normal example next
 * to a warning-sign example, a magnifier sweeping to the changed line). Portaled
 * to <body> so the 3D monitor overlay never clips it. */
function InspectHelpModal({ item, onClose }: { item: InspectItem; onClose: () => void }) {
  const h = item.help ?? { normal: "A normal detail", warning: "A warning sign" };
  return createPortal(
    <div className="inv-help-ov" role="dialog" aria-label={`What to look for: ${item.label}`} onClick={onClose}>
      <div className="inv-help" onClick={(e) => e.stopPropagation()}>
        <header className="inv-help-h">
          <div><span className="inv-help-kicker">What to look for</span><b>{item.label}</b></div>
          <button type="button" className="inv-help-x" aria-label="Close" onClick={onClose}>{"\u2715"}</button>
        </header>
        <svg className="inv-help-svg" viewBox="0 0 360 150" role="img" aria-label="Normal example next to a warning-sign example">
          <g className="inv-help-panel">
            <rect className="inv-help-card ok" x="8" y="26" width="156" height="104" rx="12" />
            <text className="inv-help-t ok" x="24" y="48">Normal</text>
            <rect className="inv-help-line" x="24" y="62" width="104" height="8" rx="4" />
            <rect className="inv-help-line hi-ok" x="24" y="80" width="124" height="9" rx="4" />
            <rect className="inv-help-line" x="24" y="99" width="78" height="8" rx="4" />
          </g>
          <g className="inv-help-panel">
            <rect className="inv-help-card bad" x="196" y="26" width="156" height="104" rx="12" />
            <text className="inv-help-t bad" x="212" y="48">Warning sign</text>
            <rect className="inv-help-line" x="212" y="62" width="104" height="8" rx="4" />
            <rect className="inv-help-line hi-bad" x="212" y="80" width="124" height="9" rx="4" />
            <rect className="inv-help-line" x="212" y="99" width="78" height="8" rx="4" />
          </g>
          <g className="inv-help-glass"><circle cx="0" cy="0" r="15" /><line x1="11" y1="11" x2="21" y2="21" /></g>
        </svg>
        <div className="inv-help-legend">
          <div className="inv-help-ex is-ok"><span className="inv-help-badge">{"\u2713"}</span><span>{h.normal}</span></div>
          <div className="inv-help-ex is-bad"><span className="inv-help-badge">{"\u2715"}</span><span>{h.warning}</span></div>
        </div>
        <p className="inv-help-tip">{item.tip}</p>
        <div className="inv-help-foot"><button type="button" className="inv-btn inv-btn--primary" onClick={onClose}>Got it</button></div>
      </div>
    </div>,
    document.body,
  );
}

export function Invoice({
  config,
  onDone,
  onGuideChange,
  onRecord,
}: {
  config: InvoiceConfig;
  onDone: (d: MfitDone) => void;
  onGuideChange?: MfitGuideFn;
  onRecord?: (eventId: string, evidence?: string) => void;
  screenBox?: { leftPct: number; topPct: number; widthPct: number; heightPct: number; visible: boolean } | null;
}) {
  const doneRef = useRef(false);
  const finish = (d: MfitDone) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone(d);
  };
  const rec = (eventId?: string, evidence?: string) => {
    if (eventId) onRecord?.(eventId, evidence);
  };

  if (config.mode === "briefing") return <BriefingStage config={config} finish={finish} onGuideChange={onGuideChange} />;
  if (config.mode === "film") return <FilmStage config={config} finish={finish} onGuideChange={onGuideChange} />;
  if (config.mode === "finance") return <FinanceStage config={config} finish={finish} rec={rec} onGuideChange={onGuideChange} />;
  if (config.mode === "payment") return <PaymentStage config={config} finish={finish} rec={rec} onGuideChange={onGuideChange} />;
  if (config.mode === "attackerIntro") return <AttackerIntroStage config={config} finish={finish} onGuideChange={onGuideChange} />;
  if (config.mode === "incident") return <IncidentStage config={config} finish={finish} rec={rec} onGuideChange={onGuideChange} />;
  if (config.mode === "summary") return <SummaryStage config={config} finish={finish} />;
  return null;
}

const deskCfg = (clock?: string, date?: string): DesktopConfig => ({ clock, date, icons: DESK_APPS, taskbar: DESK_APPS });

// ---------------------------------------------------------------- PC: security briefing window
function BriefingStage({ config, finish, onGuideChange }: { config: InvoiceConfig; finish: (d: MfitDone) => void; onGuideChange?: MfitGuideFn }) {
  const b = config.briefing!;
  useEffect(() => {
    onGuideChange?.(config.guide?.briefing ?? null);
    return () => onGuideChange?.(null);
  }, [config.guide, onGuideChange]);
  return (
    <Desktop config={deskCfg(config.clock, config.date)} className="inv-desk" openAppIds={["outlook"]} activeAppId="outlook" onOpen={() => undefined}>
      <div className="inv-win">
        <WindowTitlebar title={"Bellmont Museum \u2014 Security briefing"} />
        <div className="inv-page inv-brief-win">
          <div className={`inv-brief-body${b.lines ? " has-facts" : ""}`}>
            <div className="inv-brief-copy">
              {b.kicker && <span className="inv-brief-kicker">{b.kicker}</span>}
              <h2>{b.title}</h2>
              {b.body && b.body.map((p) => <p key={p}>{p}</p>)}
            </div>
            {b.lines && (
              <dl className="inv-brief-facts">
                {b.lines.map((l) => (<div key={l.label}><dt>{l.label}</dt><dd>{l.value}</dd></div>))}
              </dl>
            )}
          </div>
          <div className="inv-sticky">
            <button type="button" className="inv-btn inv-btn--primary cf-pulse-target" onClick={() => { audio.ui("pageOpen"); finish({ key: "continue" }); }}>{b.cta}</button>
          </div>
        </div>
      </div>
    </Desktop>
  );
}

// ---------------------------------------------------------------- PC: awareness film (browser window)
function FilmStage({ config, finish, onGuideChange }: { config: InvoiceConfig; finish: (d: MfitDone) => void; onGuideChange?: MfitGuideFn }) {
  const f = config.film!;
  const [phase, setPhase] = useState<"idle" | "playing" | "outro">("idle");
  const outroRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const voiceOpts = { voice: f.outroVoiceId ?? "nova", lang: activeTtsLang() };
  useEffect(() => {
    if (f.outroLine) void audio.prefetchSpeech(f.outroLine, voiceOpts);
    return () => { if (videoRef.current) audio.detachFilm(videoRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    onGuideChange?.(config.guide?.[phase === "idle" ? "film_intro" : phase === "playing" ? "film_playing" : "film_done"] ?? null);
    return () => onGuideChange?.(null);
  }, [phase, config.guide, onGuideChange]);
  const done = (watched: boolean) => {
    if (outroRef.current) return;
    outroRef.current = true;
    setPhase("outro");
    const go = () => finish({ key: watched ? "watched" : "skipped", evidence: watched ? "Watched the awareness film" : "Skipped the awareness film" });
    if (f.outroLine) void audio.speak(f.outroLine, voiceOpts).then(go);
    else go();
  };
  return (
    <Desktop config={deskCfg(config.clock, config.date)} className="inv-desk" openAppIds={["browser"]} activeAppId="browser" onOpen={() => undefined}>
      <div className="inv-win">
        <WindowTitlebar title="Chrome" />
        <div className="inv-addr"><span className="inv-addr-nav" aria-hidden="true">{"\u2039 \u203A"}</span><span className="inv-addr-url"><i aria-hidden="true">{"\u{1F512}"}</i>intranet.bellmontmuseum.ch/security-awareness</span></div>
        <div className="inv-film-browser">
          {phase === "playing" ? (
            <div className="inv-film-video">
              <video ref={videoRef} src={f.src} poster={f.poster} controls autoPlay playsInline onPlay={() => videoRef.current && audio.attachFilm(videoRef.current, 6)} onEnded={() => done(true)} />
              <button type="button" className="inv-btn inv-film-skip" onClick={() => done(false)}>{f.skipLabel ?? "Skip the video"}</button>
            </div>
          ) : (
            <div className="inv-film-cover" style={f.poster ? { backgroundImage: `url(${f.poster})` } : undefined}>
              <div className="inv-film-card">
                {f.kicker && <span className="inv-film-kicker">{f.kicker}</span>}
                <h2>{f.title}</h2>
                <p>{f.intro}</p>
                <div className="inv-film-btns">
                  <button
                    type="button"
                    className={`inv-btn inv-btn--primary${phase === "idle" ? " cf-pulse-target" : ""}`}
                    disabled={phase === "outro"}
                    title={phase === "outro" ? "Please wait for the briefing to finish." : undefined}
                    onClick={() => setPhase("playing")}
                  >{f.watchLabel ?? "Watch the video"}</button>
                  {phase === "idle" ? (
                    <button type="button" className="inv-btn" onClick={() => done(false)}>{f.skipLabel ?? "Skip the video"}</button>
                  ) : (
                    <span className="inv-voicebox" role="status" aria-label="Voice speaking, please wait"><VoiceBars /><span>{"Speaking\u2026"}</span></span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Desktop>
  );
}

// ---------------------------------------------------------------- attacker intro (narrated card)
function AttackerIntroStage({ config, finish, onGuideChange }: { config: InvoiceConfig; finish: (d: MfitDone) => void; onGuideChange?: MfitGuideFn }) {
  const a = config.attackerIntro!;
  const [lineIdx, setLineIdx] = useState(-1);
  const [doneSpeaking, setDoneSpeaking] = useState(false);
  const alive = useRef(true);
  const voiceOpts = { voice: a.voiceId ?? "onyx", lang: activeTtsLang() };
  useEffect(() => {
    onGuideChange?.(config.guide?.attackerIntro ?? null);
    alive.current = true;
    a.lines.forEach((l) => void audio.prefetchSpeech(l, voiceOpts));
    void (async () => {
      await new Promise((r) => window.setTimeout(r, 400));
      for (let i = 0; i < a.lines.length; i++) {
        if (!alive.current) return;
        setLineIdx(i);
        await audio.speak(a.lines[i], voiceOpts);
        await new Promise((r) => window.setTimeout(r, 250));
      }
      if (alive.current) setDoneSpeaking(true);
    })();
    return () => { alive.current = false; audio.cancelSpeech(); onGuideChange?.(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const skip = () => { alive.current = false; audio.cancelSpeech(); setDoneSpeaking(true); };
  const revealed = doneSpeaking ? a.lines.length - 1 : lineIdx;
  return (
    <div className="inv-attacker-stage">
      <div className="inv-attacker">
        <span className="inv-attacker-kicker">{a.kicker ?? "Attacker view"}</span>
        <h2>{a.title}</h2>
        <div className="inv-attacker-lines">
          {a.lines.map((l, i) => (
            <p key={i} className={i <= revealed || doneSpeaking ? "is-in" : ""}>{l}</p>
          ))}
        </div>
        <div className="inv-attacker-foot">
          {!doneSpeaking ? (
            <>
              <span className="inv-voicebox" role="status"><VoiceBars /><span>{"Speaking\u2026"}</span></span>
              <button type="button" className="inv-btn inv-attacker-skip" onClick={skip}>Skip</button>
            </>
          ) : (
            <Next on onClick={() => finish({ key: "continue" })} chip="Click here">{a.cta ?? "Continue"}</Next>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- finance workstation (PC)
type FinanceView = "mail" | "mail_read" | "procurement" | "original" | "revised_read" | "inspect" | "verify" | "verify_denied" | "teams";

function FinanceStage({ config, finish, rec, onGuideChange }: { config: InvoiceConfig; finish: (d: MfitDone) => void; rec: (id?: string, ev?: string) => void; onGuideChange?: MfitGuideFn }) {
  const fc = config.finance!;
  const ev = config.events ?? {};
  const [view, setView] = useState<FinanceView>("mail");
  const [revisedArrived, setRevisedArrived] = useState(false);
  const [inspected, setInspected] = useState(false);
  const [calledTrusted, setCalledTrusted] = useState(false);
  const [calledRevised, setCalledRevised] = useState(false);
  const [repliedSeen, setRepliedSeen] = useState(false);
  const [reportedDone, setReportedDone] = useState(false);
  const [usedChannel, setUsedChannel] = useState(false);
  const [teamsPicked, setTeamsPicked] = useState(false);
  const [teamsSent, setTeamsSent] = useState<string | null>(null);
  const [callKind, setCallKind] = useState<"revised" | "trusted" | null>(null);
  const once = useRef<Record<string, boolean>>({});
  const claireRef = useRef<HTMLButtonElement>(null);
  const lenaRef = useRef<HTMLButtonElement>(null);
  const voice = (id?: string) => ({ voice: id ?? "nova", lang: activeTtsLang() });

  useEffect(() => {
    void audio.prefetchSpeech(fc.revisedCall, voice(fc.people.accounts.voiceId));
    void audio.prefetchSpeech(fc.trustedCall, voice(fc.people.lena.voiceId));
    void audio.prefetchSpeech(fc.replyLine, voice(fc.people.lena.voiceId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guideKey = callKind ? "call" : view;
  const nextIsPayment = !callKind && (usedChannel || teamsPicked);
  useEffect(() => {
    let g = config.guide?.[guideKey] ?? null;
    if (nextIsPayment && (guideKey === "verify" || guideKey === "verify_denied" || guideKey === "teams")) {
      g = { title: "Next: payment review", body: "You have checked where the money really goes. Click Go to payment review to decide on the payment.", actionLabel: "Go to payment review" };
    }
    onGuideChange?.(g);
    return () => onGuideChange?.(null);
  }, [guideKey, nextIsPayment, config.guide, onGuideChange]);

  const guard = (flag: string, fn: () => void) => {
    if (once.current[flag]) return;
    once.current[flag] = true;
    fn();
  };
  const denied = calledTrusted;
  const verifyView: FinanceView = denied ? "verify_denied" : "verify";

  const openCall = (kind: "revised" | "trusted") => {
    setUsedChannel(true);
    setCallKind(kind);
    if (kind === "revised") {
      setCalledRevised(true);
      guard("called_revised", () => rec(ev.called_revised, "Called the number on the revised invoice"));
      void audio.speak(fc.revisedCall, voice(fc.people.accounts.voiceId));
    } else {
      guard("called_trusted", () => rec(ev.called_trusted, "Called the supplier on the number already on file"));
      void audio.speak(fc.trustedCall, voice(fc.people.lena.voiceId));
    }
  };
  const endCall = () => {
    audio.cancelSpeech();
    const wasTrusted = callKind === "trusted";
    setCallKind(null);
    if (wasTrusted) setCalledTrusted(true);
    setView(wasTrusted ? "verify_denied" : "verify");
  };

  const go = (v: FinanceView) => { audio.ui("pageOpen"); setView(v); };
  const claireHot = view === "mail" && !revisedArrived;
  const lenaHot = view === "mail" && revisedArrived;
  const onInspectTab = view === "inspect" || view === "revised_read";
  const onVerifyTab = view === "verify" || view === "verify_denied";
  const tabs: { id: FinanceView; label: string; show: boolean; on: boolean }[] = [
    { id: "mail", label: "Mail", show: true, on: view === "mail" || view === "mail_read" },
    { id: "procurement", label: "Procurement", show: true, on: view === "procurement" || view === "original" },
    { id: "inspect", label: "Inspect invoice", show: revisedArrived, on: onInspectTab },
    { id: verifyView, label: "Verify", show: inspected || onVerifyTab, on: onVerifyTab },
  ];

  const person = callKind === "revised" ? fc.people.accounts : fc.people.lena;
  const callLine = callKind === "revised" ? fc.revisedCall : fc.trustedCall;

  return (
    <Desktop config={deskCfg(config.clock, config.date)} className="inv-desk" openAppIds={["outlook"]} activeAppId="outlook" onOpen={() => undefined}>
      <div className="inv-win">
        <WindowTitlebar title="Bellmont Operations" />
        <div className="inv-tabs">
          {tabs.filter((t) => t.show).map((t) => (
            <button key={t.label} type="button" className={`inv-tab${t.on ? " is-on" : ""}`} onClick={() => go(t.id)}>{t.label}</button>
          ))}
          <span className="inv-tabs-pay">
            <button type="button" className={`inv-btn inv-btn--sm${inspected || nextIsPayment ? " cf-pulse-target" : ""}`} onClick={() => finish({ key: "continue" })}>Go to payment review</button>
          </span>
        </div>
        <div className="inv-page">
          {(view === "mail" || view === "mail_read" || view === "revised_read") && (
            <div className="inv-mail">
              <ul className="inv-maillist">
                <li>
                  <span className={`inv-next inv-mailrow-wrap${claireHot ? " is-hot" : ""}`}>
                    <button ref={claireRef} type="button" className={`inv-mailrow${view === "mail_read" ? " is-sel" : ""}${claireHot ? " cf-pulse-target" : ""}`} onClick={() => go("mail_read")}>
                      <span className="inv-mail-ava" style={{ background: "#8a5a2b" }}>CD</span>
                      <span className="inv-mail-meta"><b>{fc.people.claire.name}</b><small>{fc.mailClaire.subject}</small></span>
                      <span className="inv-mail-time">{fc.mailClaire.time}</span>
                    </button>
                    {claireHot && <Chip targetRef={claireRef} onClick={() => go("mail_read")} />}
                  </span>
                </li>
                {revisedArrived && (
                  <li>
                    <span className={`inv-next inv-mailrow-wrap${lenaHot ? " is-hot" : ""}`}>
                      <button ref={lenaRef} type="button" className={`inv-mailrow${view === "revised_read" ? " is-sel" : ""}${lenaHot ? " cf-pulse-target" : ""}`} onClick={() => go("revised_read")}>
                        <span className="inv-mail-ava" style={{ background: "#2f6f6b" }}>LV</span>
                        <span className="inv-mail-meta"><b>{fc.people.lena.name}</b><small>{fc.mailRevised.subject}</small></span>
                        <span className="inv-mail-time">{fc.mailRevised.time}</span>
                      </button>
                      {lenaHot && <Chip targetRef={lenaRef} onClick={() => go("revised_read")} />}
                    </span>
                  </li>
                )}
              </ul>
              <div className="inv-mailread">
                {view === "mail_read" && (
                  <>
                    <header><b>{fc.mailClaire.subject}</b><span>{fc.mailClaire.from} {DOT} {fc.mailClaire.time}</span></header>
                    <pre className="inv-mailbody">{fc.mailClaire.body}</pre>
                    <div className="inv-sticky">
                      <Next on onClick={() => { audio.ui("docOpen"); go("procurement"); }}>Open the procurement record</Next>
                    </div>
                  </>
                )}
                {view === "revised_read" && (
                  <>
                    <header><b>{fc.mailRevised.subject}</b><span>{fc.mailRevised.from} {DOT} {fc.mailRevised.time}</span></header>
                    <pre className="inv-mailbody">{fc.mailRevised.body}</pre>
                    <div className="inv-attach"><span aria-hidden="true">PDF</span>{fc.mailRevised.attachment}</div>
                    <div className="inv-sticky">
                      <Next on onClick={() => { audio.ui("docOpen"); go("inspect"); }}>Inspect the invoice</Next>
                    </div>
                  </>
                )}
                {view === "mail" && <div className="inv-empty">Select a message to read it.</div>}
              </div>
            </div>
          )}

          {view === "procurement" && (
            <div className="inv-doc">
              <h3>Bellmont Procurement {"\u2014"} supplier account</h3>
              <dl className="inv-fields">
                <div><dt>Supplier</dt><dd>{fc.fix.supplier}</dd></div>
                <div><dt>Supplier status</dt><dd>Approved</dd></div>
                <div><dt>Invoice</dt><dd>{fc.fix.invoiceId}</dd></div>
                <div><dt>Purchase order</dt><dd>{fc.fix.po}</dd></div>
                <div><dt>Amount</dt><dd>{fc.fix.amount}</dd></div>
                <div><dt>Purpose</dt><dd>{fc.fix.purpose}</dd></div>
                <div><dt>Approved payee</dt><dd>{fc.fix.approvedBank}</dd></div>
                <div><dt>Supplier contact on file</dt><dd>{fc.fix.trustedNumber}</dd></div>
              </dl>
              <div className="inv-sticky">
                <Next on onClick={() => {
                  guard("read_original", () => rec(ev.read_original, "Checked the approved invoice and supplier record"));
                  audio.ui("docOpen"); go("original");
                }}>View original invoice</Next>
              </div>
            </div>
          )}

          {view === "original" && (
            <div className="inv-doc">
              <div className="inv-invoice">
                <div className="inv-invoice-head"><b>{fc.fix.supplier}</b><span>Invoice {fc.fix.invoiceId}</span></div>
                <dl className="inv-fields">
                  <div><dt>Purchase order</dt><dd>{fc.fix.po}</dd></div>
                  <div><dt>Service</dt><dd>{fc.fix.purpose}</dd></div>
                  <div><dt>Amount</dt><dd>{fc.fix.amount}</dd></div>
                  <div><dt>Bank account</dt><dd>{fc.fix.approvedBank}</dd></div>
                  <div><dt>Contact</dt><dd>Approved supplier contact</dd></div>
                </dl>
              </div>
              <div className="inv-sticky">
                <Next on onClick={() => {
                  if (!revisedArrived) { setRevisedArrived(true); audio.ui("message"); }
                  go("mail");
                }}>Back to the inbox</Next>
              </div>
            </div>
          )}

          {view === "inspect" && (
            <InspectionView
              items={fc.inspection ?? []}
              intro={fc.inspectionIntro}
              onDetect={() => guard("detected", () => rec(ev.detected, "Flagged the changed bank account as suspicious"))}
              onDone={() => { setInspected(true); go("verify"); }}
            />
          )}

          {(view === "verify" || view === "verify_denied") && (
            <div className="inv-verify">
              <h3>Verify the bank-account change</h3>
              {denied && <div className="inv-dispute">The supplier denied the change on the trusted number. The revised invoice is flagged DISPUTED.</div>}
              <p className="inv-verify-lede">You found a changed bank account. Confirm where the money is really going before paying. Pick a way to check.</p>
              <div className="inv-opts inv-channels">
                <div className={`inv-channel${repliedSeen ? " is-used" : ""}`}>
                  <b><Icon name="mail" />Reply in the email thread</b>
                  <small>Ask Lena in the same message to confirm the new account.</small>
                  {repliedSeen ? (
                    <span className="inv-channel-result">Replied in the thread {DOT} the mailbox {"\u201c"}confirmed{"\u201d"} it. A reply to the same mailbox is not independent proof.</span>
                  ) : (
                    <button type="button" className="inv-opt-btn" onClick={() => {
                      setUsedChannel(true);
                      guard("replied", () => rec(ev.replied, "Replied in the thread and got a 'confirmed' answer"));
                      void audio.speak(fc.replyLine, voice(fc.people.lena.voiceId));
                      setRepliedSeen(true);
                    }}><Icon name="mail" />Reply and ask</button>
                  )}
                </div>
                <div className={`inv-channel${calledRevised ? " is-used" : ""}`}>
                  <b><Icon name="phone" />Call the number on the revised invoice</b>
                  <small>{fc.fix.revisedNumber}</small>
                  {calledRevised ? (
                    <span className="inv-channel-result">Called the number on the invoice {DOT} it {"\u201c"}confirmed{"\u201d"} the change. A number printed on the invoice is not independent.</span>
                  ) : (
                    <button type="button" className="inv-opt-btn" onClick={() => openCall("revised")}><Icon name="phone" />Call this number</button>
                  )}
                </div>
                <div className={`inv-channel${calledTrusted ? " is-used" : ""}`}>
                  <b><Icon name="phone" />Call the supplier on the number already on file</b>
                  <small>{fc.fix.trustedNumber}</small>
                  {calledTrusted ? (
                    <span className="inv-channel-result">Called the supplier on file {DOT} they DENIED the change.</span>
                  ) : (
                    <button type="button" className="inv-opt-btn" onClick={() => openCall("trusted")}><Icon name="phone" />Call the contact on file</button>
                  )}
                </div>
              </div>
              {denied && (
                <div className="inv-verify-actions">
                  <button type="button" className={`inv-btn inv-btn--report${reportedDone ? " is-done" : ""}`} disabled={reportedDone} onClick={() => { guard("reported", () => rec(ev.reported, "Reported the suspected invoice fraud")); setReportedDone(true); }}><Icon name="flag" />{reportedDone ? "Reported \u2713" : "Report suspected fraud"}</button>
                  <button type="button" className="inv-btn inv-btn--reply" onClick={() => go("teams")}><Icon name="chat" />Reply to Claire</button>
                </div>
              )}
              {nextIsPayment && (
                <div className="inv-sticky inv-verify-next">
                  <Next on onClick={() => finish({ key: "continue" })}>Go to payment review</Next>
                </div>
              )}
            </div>
          )}

          {view === "teams" && (
            <div className="inv-teams">
              <header className="inv-teams-h">
                <span className="inv-teams-av">CD</span>
                <div><b>{fc.people.claire.name}</b><small>{fc.people.claire.role}</small></div>
              </header>
              <div className="inv-teams-msg">{fc.teamsNudge}</div>
              {teamsSent ? (
                <>
                  <div className="inv-teams-sent"><span className="inv-teams-bubble">{teamsSent}</span><span className="inv-teams-sent-meta">Sent to {fc.people.claire.name} {"\u2713"}</span></div>
                  <div className="inv-sticky inv-verify-next">
                    <Next on onClick={() => finish({ key: "continue" })}>Go to payment review</Next>
                  </div>
                </>
              ) : (
                <>
                  <span className="inv-pick">Choose your reply:</span>
                  <div className={`inv-opts inv-teams-replies${teamsPicked ? "" : " is-sweep"}`}>
                    {fc.teamsReplies.map((r) => {
                      const need = r.needsTrusted && !denied;
                      return (
                        <button key={r.id} type="button" className="inv-opt inv-teams-reply" disabled={need} onClick={() => {
                          setTeamsPicked(true);
                          setTeamsSent(r.label);
                          if (r.eventId) guard(`teams_${r.id}`, () => rec(r.eventId, "Kept the director informed without giving in to pressure"));
                        }}>{r.label}{need ? " (call the trusted contact first)" : ""}</button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {callKind && (
          <div className="inv-call">
            <div className="inv-call-card">
              {person.avatarUrl ? <img className="inv-call-av" src={person.avatarUrl} alt="" /> : <span className="inv-call-av is-ph">{person.name.slice(0, 1)}</span>}
              <div className="inv-call-id"><b>{person.name}</b><small>{callKind === "revised" ? "Caller ID from the revised invoice" : "Verified contact on file"}</small></div>
              <span className="inv-call-live"><i aria-hidden="true" />Live</span>
            </div>
            <div className="inv-call-line">{callLine}</div>
            <Next on onClick={endCall}>End call</Next>
          </div>
        )}
      </div>
    </Desktop>
  );
}

function InspectionView({ items, intro, onDetect, onDone }: { items: InspectItem[]; intro?: string; onDetect: () => void; onDone: () => void }) {
  const [idx, setIdx] = useState(0);
  const [answered, setAnswered] = useState<Record<string, "suspicious" | "ok">>({});
  const [showTip, setShowTip] = useState(false);
  const [help, setHelp] = useState<InspectItem | null>(null);
  const item = items[idx];
  const a = item ? answered[item.id] : undefined;
  const correctOf = (it?: InspectItem): "suspicious" | "ok" => it?.correct ?? (it?.isChange ? "suspicious" : "ok");
  const isRight = !!a && a === correctOf(item);
  const answer = (choice: "suspicious" | "ok") => {
    if (!item || a) return;
    audio.ui(choice === correctOf(item) ? "success" : "error");
    setAnswered((m) => ({ ...m, [item.id]: choice }));
    if (item.isChange && choice === "suspicious") onDetect();
  };
  const next = () => { setShowTip(false); if (idx + 1 < items.length) setIdx(idx + 1); else onDone(); };
  const last = idx + 1 >= items.length;
  return (
    <div className="inv-insp">
      <ol className="inv-insp-nav" aria-label="Inspection checklist">
        {items.map((it, i) => (
          <li key={it.id} className={`inv-insp-navitem${i === idx ? " is-active" : ""}${answered[it.id] ? " is-done" : ""}`}>
            <span className="inv-insp-navdot" aria-hidden="true">{answered[it.id] ? "\u2713" : i + 1}</span>
            <span className="inv-insp-navlabel">{it.label}</span>
          </li>
        ))}
      </ol>
      <div className="inv-insp-main">
        <div className="inv-insp-doc-h"><span className="inv-insp-pdf" aria-hidden="true">PDF</span>Revised invoice AT-4021</div>
        <div className="inv-insp-step">Item {idx + 1} of {items.length}</div>
        <h4 className="inv-insp-label">{item?.label}</h4>
        <div className="inv-insp-value">
          {item?.isChange ? (
            <>
              <div className="inv-insp-kv"><span>On file</span><b>{item.approved}</b></div>
              <div className="inv-insp-kv"><span>On this invoice</span><b>{item.requested}</b></div>
            </>
          ) : (
            <p>{item?.value}</p>
          )}
        </div>
        {!a ? (
          <>
            {idx === 0 && intro && <p className="inv-insp-intro">{intro}</p>}
            <p className="inv-insp-q">Is this a normal detail, or a warning sign?</p>
            <div className="inv-insp-btns">
              <button type="button" className="inv-btn inv-insp-ans sus" onClick={() => answer("suspicious")}>Suspicious</button>
              <button type="button" className="inv-btn inv-insp-ans ok" onClick={() => answer("ok")}>Looks OK</button>
            </div>
            <button type="button" className="inv-insp-tip-btn" onClick={() => setShowTip((v) => !v)}>{showTip ? "Hide tip" : "Tip: what to look for"}</button>
            {showTip && <div className="inv-insp-tip">{item?.tip}</div>}
          </>
        ) : (
          <>
            <div className={`inv-insp-res is-${isRight ? "right" : "wrong"}`}>
              <span className="inv-insp-res-ic" aria-hidden="true">{isRight ? "\u2713" : "\u2715"}</span>
              <div className="inv-insp-res-body">
                <b>{isRight ? "Well done" : "Not quite"}</b>
                <p>{a === "suspicious" ? item?.suspiciousFeedback : item?.okFeedback}</p>
              </div>
            </div>
            <button type="button" className="inv-insp-help-btn" onClick={() => item && setHelp(item)}><Icon name="help" />What to look for</button>
            <div className="inv-sticky inv-insp-next">
              <Next on onClick={next}>{last ? "Done \u2014 verify the change" : "Next item"}</Next>
            </div>
          </>
        )}
      </div>
      {help && <InspectHelpModal item={help} onClose={() => setHelp(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------- payment review (PC)
type PayChoice = "hold" | "report" | "original" | "revised";
function PaymentStage({ config, finish, rec, onGuideChange }: { config: InvoiceConfig; finish: (d: MfitDone) => void; rec: (id?: string, ev?: string) => void; onGuideChange?: MfitGuideFn }) {
  const p = config.payment!;
  const ev = config.events ?? {};
  const replay = !!config.replay;
  const disputed = recorded().has("inv_called_trusted");
  const [stage, setStage] = useState<"review" | "blocked" | "processing">("review");
  const [choice, setChoice] = useState<PayChoice | null>(replay ? "revised" : null);
  const [blockPhase, setBlockPhase] = useState<"run" | "done">("run");
  useEffect(() => {
    onGuideChange?.(config.guide?.[stage === "processing" ? "processing" : stage === "blocked" ? "blocked" : "review"] ?? null);
    return () => onGuideChange?.(null);
  }, [stage, config.guide, onGuideChange]);
  useEffect(() => {
    if (stage !== "blocked" || blockPhase !== "run") return;
    const t = window.setTimeout(() => setBlockPhase("done"), 1900);
    return () => window.clearTimeout(t);
  }, [stage, blockPhase]);

  const options: { id: PayChoice; title: string; sub: string }[] = [
    { id: "hold", title: "Hold the payment", sub: "Keep it on hold until the account change is verified." },
    { id: "report", title: "Hold and report suspected fraud", sub: "Hold the payment and flag it to Finance and IT." },
    { id: "original", title: "Release the approved account on file", sub: `Pay the account on file (${p.fix.approvedSuffix}) under normal controls.` },
    { id: "revised", title: "Release the payment to the new account", sub: `Pay the newly requested account (${p.fix.revisedSuffix}).` },
  ];

  const runRevised = () => {
    if (!replay && disputed) { audio.ui("reminder"); setBlockPhase("run"); setStage("blocked"); return; }
    audio.ui("reminder");
    setStage("processing");
    if (!replay) rec(ev.redirected, "Submitted the changed bank account without independent verification");
    window.setTimeout(() => finish({ key: replay ? "replay" : "redirected" }), 2600);
  };
  const confirm = () => {
    if (!choice) return;
    if (choice === "revised") { runRevised(); return; }
    if (choice === "hold") { rec(ev.held, "Held the unverified payment"); finish({ key: "held" }); return; }
    if (choice === "report") { rec(ev.reported, "Reported the suspected invoice fraud"); rec(ev.held, "Held the unverified payment"); finish({ key: "reported" }); return; }
    if (choice === "original") { rec(ev.original, "Paid the approved account under normal controls"); finish({ key: "original" }); return; }
  };

  return (
    <Desktop config={deskCfg("17:52", "Opening day")} className="inv-desk" openAppIds={["pdf"]} activeAppId="pdf" onOpen={() => undefined}>
      <div className="inv-win">
        <WindowTitlebar title="Bellmont Finance" />
        <div className="inv-page inv-pay">
          <h3>Payment review</h3>
          <dl className="inv-fields">
            <div><dt>Supplier</dt><dd>{p.fix.supplier}</dd></div>
            <div><dt>Invoice</dt><dd>{p.fix.invoiceId} {DOT} PO {p.fix.po}</dd></div>
            <div><dt>Amount</dt><dd>{p.fix.amount}</dd></div>
            <div><dt>Approved account</dt><dd>{p.fix.approvedBank}</dd></div>
            <div className={disputed && !replay ? "is-warn" : ""}><dt>Requested account</dt><dd>{p.fix.revisedBank}</dd></div>
            <div className={disputed && !replay ? "is-warn" : ""}><dt>Bank-account change</dt><dd>{replay ? "Not verified" : disputed ? "Disputed by supplier" : "Not verified"}</dd></div>
          </dl>

          {stage === "processing" && (
            <div className="inv-processing">
              <span className="inv-spin" aria-hidden="true" />
              <ol>
                <li>Payment instruction submitted</li>
                <li>Simulated bank transfer processed</li>
                <li className="is-bad">Funds credited to the account ending 7785</li>
              </ol>
            </div>
          )}

          {stage === "review" && (
            <div className="inv-pay-actions">
              {!replay && <span className="inv-pick">Based on what you found, choose one action:</span>}
              <div className={`inv-opts inv-pay-grid${!replay && !choice ? " is-sweep" : ""}`}>
                {(replay ? options.filter((o) => o.id === "revised") : options).map((o) => (
                  <button key={o.id} type="button" className={`inv-opt${choice === o.id ? " is-sel" : ""}`} onClick={() => setChoice(o.id)}>
                    <b>{o.title}</b>
                    <small>{o.sub}</small>
                  </button>
                ))}
              </div>
              <div className="inv-pay-confirm">
                {choice ? (
                  <Next on onClick={confirm}>Confirm this action</Next>
                ) : (
                  <button type="button" className="inv-btn inv-btn--primary" disabled>Confirm this action</button>
                )}
              </div>
            </div>
          )}

          {stage === "blocked" && (
            <div className="inv-ebank">
              <div className="inv-ebank-top"><span className="inv-ebank-logo">Bellmont Bank {DOT} Online payment</span></div>
              <dl className="inv-ebank-fields">
                <div><dt>Beneficiary</dt><dd>{p.fix.supplier}</dd></div>
                <div><dt>Account</dt><dd>{p.fix.revisedBank}</dd></div>
                <div><dt>Amount</dt><dd>{p.fix.amount}</dd></div>
              </dl>
              {blockPhase === "run" ? (
                <div className="inv-ebank-run">
                  <span className="inv-spin" aria-hidden="true" />
                  <span>Submitting payment to the bank{"\u2026"}</span>
                  <div className="inv-ebank-bar"><i /></div>
                </div>
              ) : (
                <>
                  <div className="inv-ebank-reject" role="alert">
                    <span className="inv-ebank-x" aria-hidden="true">{"\u2715"}</span>
                    <div><b>Payment stopped</b><p>The beneficiary change for this supplier is disputed and needs verification. No money has left the account.</p></div>
                  </div>
                  <div className="inv-sticky">
                    <Next on onClick={() => { setBlockPhase("run"); setChoice(null); setStage("review"); }}>Back to the payment options</Next>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </Desktop>
  );
}

// ---------------------------------------------------------------- incident response (PC)
function IncidentStage({ config, finish, rec, onGuideChange }: { config: InvoiceConfig; finish: (d: MfitDone) => void; rec: (id?: string, ev?: string) => void; onGuideChange?: MfitGuideFn }) {
  const ic = config.incident!;
  const [done, setDone] = useState<Set<string>>(new Set());
  useEffect(() => {
    onGuideChange?.(config.guide?.incident ?? null);
    return () => onGuideChange?.(null);
  }, [config.guide, onGuideChange]);
  const priorityDone = ic.actions.some((a) => a.priority && done.has(a.id));
  return (
    <Desktop config={deskCfg("17:54", "Opening day")} className="inv-desk" openAppIds={["outlook"]} activeAppId="outlook" onOpen={() => undefined}>
      <div className="inv-win">
        <WindowTitlebar title={"Bellmont \u2014 incident response"} />
        <div className="inv-page inv-incident">
          <div className="inv-incident-note">{ic.note}</div>
          <p className="inv-incident-lede">{ic.intro}</p>
          <ul className="inv-incident-list">
            {ic.actions.map((a) => {
              const isDone = done.has(a.id);
              return (
                <li key={a.id} className={`${a.priority ? "is-priority" : ""}${isDone ? " is-done" : ""}`}>
                  <span className="inv-incident-c"><b>{a.label}{a.priority ? " \u2014 do this first" : ""}</b><small>{a.sub}</small></span>
                  <button type="button" className={`inv-btn${a.priority && !isDone ? " cf-pulse-target" : ""}`} disabled={isDone} onClick={() => {
                    audio.ui("success");
                    if (a.eventId) rec(a.eventId, a.label);
                    setDone((d) => new Set(d).add(a.id));
                  }}>{isDone ? "Done \u2713" : "Do this"}</button>
                </li>
              );
            })}
          </ul>
          <p className="inv-incident-closing">{ic.closingNote}</p>
          <div className="inv-sticky">
            <button type="button" className={`inv-btn inv-btn--primary${priorityDone ? " cf-pulse-target" : ""}`} disabled={!priorityDone} onClick={() => finish({ key: "continue" })}>{ic.continueLabel ?? "Continue"}</button>
          </div>
        </div>
      </div>
    </Desktop>
  );
}

// ---------------------------------------------------------------- summary (TV)
function SummaryStage({ config, finish }: { config: InvoiceConfig; finish: (d: MfitDone) => void }) {
  const s = config.summary!;
  const have = recorded();
  const match = (ids?: string[]) => !ids || ids.some((i) => have.has(i));
  return (
    <div className="inv-tv-stage">
      <div className="inv-tv-fill inv-summary">
        <div className="inv-summary-inner">
          {s.kicker && <span className="inv-summary-kicker">{s.kicker}</span>}
          <h2>{s.title}</h2>
          <ul className="inv-summary-rows">
            {s.rows.map((r) => {
              const v = r.variants.find((vr) => match(vr.ifEvents)) ?? r.variants[r.variants.length - 1];
              return (
                <li key={r.q} className={`is-${v.tone ?? "warn"}`}>
                  <span className="inv-summary-q">{r.q}</span>
                  <span className="inv-summary-a">{v.a}</span>
                </li>
              );
            })}
          </ul>
          <p className="inv-summary-closing">{s.closing}</p>
          <button type="button" className="inv-btn inv-btn--primary cf-pulse-target" onClick={() => finish({})}>{s.continueLabel ?? "Continue"}</button>
        </div>
      </div>
    </div>
  );
}
